import PropTypes from 'prop-types';
import { useState, useCallback, useEffect, useRef, useMemo, memo } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import logger from '../../../../../utils/logger';
import './LocationMessage.css';

/**
 * Parses a geo URI (RFC 5870) to extract latitude and longitude
 * @param {string} geoUri - URI in format "geo:lat,lon" or "geo:lat,lon,alt"
 * @returns {{ lat: number, lon: number } | null} - Coordinates or null if invalid
 */
const parseGeoUri = (geoUri) => {
  if (!geoUri || typeof geoUri !== 'string') return null;

  // Format: geo:latitude,longitude[,altitude][;u=uncertainty]
  const match = geoUri.match(/^geo:([-+]?\d+\.?\d*),([-+]?\d+\.?\d*)/);
  if (!match) return null;

  const lat = parseFloat(match[1]);
  const lon = parseFloat(match[2]);

  if (isNaN(lat) || isNaN(lon)) return null;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;

  return { lat, lon };
};

const LocationMessage = memo(({ geoUri, body, isUser = false }) => {
  const [mapError, setMapError] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  // Memoize coords to prevent recreation on every render
  const coords = useMemo(() => parseGeoUri(geoUri), [geoUri]);
  const { lat, lon } = coords || {};
  
  // Memoize directUrl to prevent handleMapClick re-creation
  const directUrl = useMemo(
    () => coords ? `https://www.openstreetmap.org/?mlat=${coords.lat}&mlon=${coords.lon}#map=15/${coords.lat}/${coords.lon}` : '',
    [coords],
  );

  // All hooks must be called unconditionally before any returns
  const handleMapClick = useCallback(() => {
    if (directUrl) {
      window.open(directUrl, '_blank', 'noopener,noreferrer');
    }
  }, [directUrl]);

  // Initialize MapLibre map
  useEffect(() => {
    if (!coords || !mapRef.current || mapInstanceRef.current) return;

    let map = null;
    try {
      // Create map instance with OpenStreetMap raster tiles
      map = new maplibregl.Map({
        container: mapRef.current,
        style: {
          version: 8,
          sources: {
            'raster-tiles': {
              type: 'raster',
              tiles: [
                'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png',
              ],
              tileSize: 256,
              attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
            },
          },
          layers: [
            {
              id: 'simple-tiles',
              type: 'raster',
              source: 'raster-tiles',
              minzoom: 0,
              maxzoom: 22,
            },
          ],
        },
        center: [coords.lon, coords.lat], // MapLibre uses [lng, lat] order
        zoom: 14,
        attributionControl: true,
      });

      // Add marker at location
      markerRef.current = new maplibregl.Marker({ color: '#FF0000' })
        .setLngLat([coords.lon, coords.lat])
        .addTo(map);

      // Add navigation controls: top-right for current user, top-left for others
      // This avoids overlap with message hover buttons which are on opposite sides
      map.addControl(new maplibregl.NavigationControl(), isUser ? 'top-right' : 'top-left');

      mapInstanceRef.current = map;
    } catch (error) {
      // Clean up partial initialization
      if (map) {
        map.remove();
      }
      logger.error('MapLibre initialization failed:', error);
      setMapError(true);
    }

    // Cleanup on unmount
    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [coords, isUser]);

  // Conditional rendering after all hooks
  if (!coords) {
    // Fallback to text display if geo URI is invalid
    return (
      <div className="location-message location-message--error">
        <div className="location-message-icon">
          <ion-icon name="location"></ion-icon>
        </div>
        <div className="location-message-text">
          <div className="location-message-label">{body || 'Location'}</div>
          <div className="location-message-coords">Invalid location data</div>
        </div>
      </div>
    );
  }

  return (
    <div className="location-message">
      <div className="location-message-map-container">
        {!mapError ? (
          <div ref={mapRef} className="location-message-map" />
        ) : (
          <div className="location-message-map-fallback">
            <ion-icon name="map"></ion-icon>
            <span>Map unavailable</span>
          </div>
        )}
      </div>
      <div className="location-message-details">
        <div className="location-message-icon">
          <ion-icon name="location"></ion-icon>
        </div>
        <div className="location-message-text">
          <div className="location-message-label">{body || 'Location'}</div>
          <div className="location-message-coords">
            {lat.toFixed(6)}, {lon.toFixed(6)}
          </div>
        </div>
        <button
          className="location-message-open-btn"
          onClick={handleMapClick}
          title="Open in OpenStreetMap"
          aria-label="Open location in OpenStreetMap"
        >
          <ion-icon name="open-outline"></ion-icon>
        </button>
      </div>
    </div>
  );
});

LocationMessage.displayName = 'LocationMessage';

LocationMessage.propTypes = {
  geoUri: PropTypes.string.isRequired,
  body: PropTypes.string,
  isUser: PropTypes.bool,
};

export default LocationMessage;
